import { defaultTo } from 'lodash';
import { Button, Heading, Image, Layout, Text } from '../../components/core';
import { IconCheck } from '../../components/icons/IconCheck';
import { IconChevronRight } from '../../components/icons/IconChevronRight';
import { RichText } from '../../components/prismic';
import { getRoute, useNavigate } from '../../routes';
import { getStylesheet } from '../../styles';
import { PathwayStep } from './PathwayTypes';

type SkillPathwayStepProps = PathwayStep & {
  isFirstStep?: boolean;
  isLastStep?: boolean;
};

export const SkillPathwayStep = ({
  module,
  section,
  nudges,
  isFirstStep = false,
  isLastStep = false,
}: SkillPathwayStepProps): JSX.Element => {
  const navigate = useNavigate();

  const moduleData = module?.data;
  const imageUrl = defaultTo(moduleData?.image?.url, '');
  const imageAlt = defaultTo(moduleData?.image?.alt, '');
  const moduleTitle = defaultTo(moduleData?.title, '');

  const sectionData = section?.data;
  const sectionTitle = defaultTo(sectionData?.title, '');
  const sectionDescription = defaultTo(sectionData?.description, []);

  const onSectionPress = (): void => {
    if (module === undefined || section === undefined) {
      return;
    }

    navigate(
      getRoute('skillPathwaySection', {
        moduleId: module.id,
        sectionId: section.id,
      }),
    );
  };

  const headingId = `pathway-section-${section?.id ?? ''}-heading`;

  return (
    <Layout.HStack
      {...styles.topStack}
      aria-labelledby={headingId}
      role="listitem"
      space={4}
      testID="skill-pathway-step"
    >
      <Layout.Box position="relative">
        {!isFirstStep && <Layout.Box {...styles.pathLineTop} />}

        {imageUrl !== '' && (
          <Image
            {...styles.image}
            alt={imageAlt}
            aria-hidden
            source={{
              uri: imageUrl,
            }}
            resizeMode="contain"
            resizeMethod="auto"
          />
        )}

        {!isLastStep && <Layout.Box {...styles.pathLineBottom} />}
      </Layout.Box>

      <Layout.VStack {...styles.rightSection} space={6}>
        <Layout.Flex {...styles.description}>
          <Layout.VStack flex={1} space={1}>
            <Layout.Flex {...styles.flipOrderForAccessibility}>
              <Heading.h4 level={2} id={headingId}>
                {sectionTitle}
              </Heading.h4>

              <Text.caption bold>{moduleTitle}</Text.caption>
            </Layout.Flex>

            <RichText styles={{ paragraph: { marginBottom: 0 } }} field={sectionDescription} />
          </Layout.VStack>

          <Button.primarySmall
            aria-label={`Visit ${sectionTitle}`}
            isLink
            onPress={onSectionPress}
            testID="pressable-go-to-pathway-section"
            rightIcon={<IconChevronRight {...styles.rightChevron} aria-hidden size={5} />}
          >
            Visit
          </Button.primarySmall>
        </Layout.Flex>

        <Layout.Flex {...styles.nudgeWrapper} aria-label="Helps with" role="list">
          {nudges.map(nudge => (
            <Layout.HStack {...styles.nudge} key={nudge.name} role="listitem" space={3}>
              <IconCheck aria-hidden size={4} />

              <Heading.h6>{nudge.name}</Heading.h6>
            </Layout.HStack>
          ))}
        </Layout.Flex>
      </Layout.VStack>
    </Layout.HStack>
  );
};

const styles = getStylesheet({
  topStack: {
    paddingRight: 4,
    width: '100%',
  },

  pathLineTop: {
    borderColor: 'secondary.300',
    borderLeftWidth: 0,
    borderRightWidth: 2,
    borderStyle: 'dashed',
    height: 6,
    position: 'absolute',
    top: -24,
    width: '50%',
    zIndex: -1,
  },

  image: {
    size: '42px',
  },

  pathLineBottom: {
    borderColor: 'secondary.300',
    borderLeftWidth: 0,
    borderRightWidth: 2,
    borderStyle: 'dashed',
    height: '100%',
    position: 'absolute',
    top: 0,
    width: '50%',
    zIndex: -1,

    _web: {
      height: 'calc(100% + 24px)',
    },
  },

  rightSection: {
    flex: 1,
  },

  description: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },

  flipOrderForAccessibility: {
    flexDirection: 'column-reverse',
    gap: 1,
  },

  rightChevron: {
    minWidth: 5,
  },

  nudgeWrapper: {
    alignItems: 'flex-start',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 2,
  },

  nudge: {
    backgroundColor: 'primary.100',
    borderRadius: '12px',
    gap: 2,
    paddingX: '8px',
    paddingY: '4px',
  },
});
